/**
 * The data path's API client with automatic failover (PLAN §10.2 endpoint
 * selection): every call goes to the manager's CURRENT primary; when it
 * fails at the network level, the manager is told, and a call that is safe
 * to repeat is resent once — to the primary the manager settles on (the same
 * endpoint recovered, or the one it failed over to).
 *
 * Safe to repeat ("idempotent, client-generated IDs"):
 *   - every GET;
 *   - row and playlist patches: each carries its own HLC and clientOpId, and
 *     per-field LWW makes applying one twice a no-op (PLAN §6.1);
 *   - soft deletes: setting `deleted` again changes nothing;
 *   - creates that carry their client-generated id (`rundownId`, `playlistId`,
 *     `productionId`, `studioId`);
 *   - playout commands: each carries its `commandId`, and a production answers
 *     a repeated one with its first answer without running it again (§6.6).
 * Anything else — a create without an id, the tenancy writes — is not resent:
 * the first attempt may have landed, and the error goes to the caller.
 *
 * HOST-AWARE (`routing.ts`): a route the primary's host does not serve goes
 * to one that does — a cloud-only route from a node's page straight to the
 * cloud, once, without the resend (the primary's failover is not its path) —
 * and when none can (no cloud, an offline session, the cloud down) the call
 * fails at once, unsent, with a `not-found` `RouteUnavailableError` naming
 * why: the callers that degrade on `not-found` keep working, and a
 * node-served page makes no request that can only 404.
 *
 * No `@acp/api-client` change was needed: it is wrapped at its `ApiClient`
 * seam, one client per endpoint.
 */
import { ApiError, type ApiClient, type RouteId, type RouteInput, type RouteOutput, type Routes } from '@acp/api-client/client.js';
import { ROUTES, routeHosts, type RouteDef } from '@acp/api-contract/routes.js';
import type { Endpoint } from './endpoints.js';
import type { ConnectivityManager } from './manager.js';
import type { RouteTarget, RouteUnavailableReason } from './routing.js';

/** How long a resend waits for a primary to come up before giving the original error back. */
export const RESEND_WAIT_MS = 20_000;

const PATCH_ROUTES: ReadonlySet<string> = new Set(['patchRows', 'patchPlaylist']);
const DELETE_ROUTES: ReadonlySet<string> = new Set(['deleteRundown', 'deletePlaylist']);

function hasClientId(body: unknown, key: string): boolean {
  return typeof body === 'object' && body !== null && typeof Reflect.get(body, key) === 'string';
}

/** Whether a call may be sent again after its first attempt may or may not have landed. */
export function isResendable(id: RouteId, body: unknown): boolean {
  if (ROUTES[id].method === 'GET') return true;
  if (PATCH_ROUTES.has(id) || DELETE_ROUTES.has(id)) return true;
  if (id === 'createRundown') return hasClientId(body, 'rundownId');
  if (id === 'createPlaylist') return hasClientId(body, 'playlistId');
  if (id === 'createProduction') return hasClientId(body, 'productionId');
  if (id === 'createStudio') return hasClientId(body, 'studioId');
  if (id === 'sendPlayoutCommand') return hasClientId(body, 'commandId');
  return false;
}

/**
 * Whether an error means "the endpoint did not answer" rather than "it
 * answered no": the fetch itself failed (a `TypeError` in browsers and Node),
 * or a gateway in front of it answered 502/503/504 without an ACP error body
 * (a proxy whose upstream is gone). A contract error — even `unavailable` —
 * is an answer.
 */
export function isUnreachable(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  return err instanceof ApiError && [502, 503, 504].includes(err.status) && err.code.startsWith('http-');
}

/**
 * A call no backend can serve from this page now (`routeTarget`), failed unsent: a `not-found` (404) as the primary
 * would have answered, so callers that degrade on `not-found` keep doing so — and one that must tell "not here" from
 * "the resource is gone" checks `instanceof RouteUnavailableError` (and its `reason`).
 */
export class RouteUnavailableError extends ApiError {
  readonly routeId: RouteId;
  readonly reason: RouteUnavailableReason;
  constructor(id: RouteId, reason: RouteUnavailableReason) {
    super(404, 'not-found', `${id} is served by ${routeHosts(ROUTES[id]).join(' and ')}, which this page cannot reach now (${reason})`, [], null);
    this.name = 'RouteUnavailableError';
    this.routeId = id;
    this.reason = reason;
  }
}

export type FailoverDeps = {
  readonly manager: Pick<ConnectivityManager, 'primary' | 'reportUnreachable' | 'whenPrimaryUp'>;
  /** The API client for one endpoint (built once per endpoint by the caller). */
  readonly clientFor: (endpoint: Endpoint) => ApiClient;
  readonly resendWaitMs: number;
  /** Where a route goes now (`routeTarget` over the manager's state and the session). */
  readonly target: (route: RouteDef) => RouteTarget;
};

export function createFailoverApi(deps: FailoverDeps): ApiClient {
  /** One attempt at `endpoint`; a failure there at the network level is reported to the manager before it is rethrown. */
  async function attempt<K extends RouteId>(endpoint: Endpoint, id: K, input: RouteInput<Routes[K]>): Promise<RouteOutput<Routes[K]>> {
    try {
      return await deps.clientFor(endpoint).call(id, input);
    } catch (err) {
      if (isUnreachable(err)) deps.manager.reportUnreachable(endpoint.id);
      throw err;
    }
  }

  async function call<K extends RouteId>(id: K, input: RouteInput<Routes[K]>): Promise<RouteOutput<Routes[K]>> {
    const target = deps.target(ROUTES[id]);
    if (!target.ok) throw new RouteUnavailableError(id, target.reason);
    const primary = deps.manager.primary();
    // Another host's route (a cloud-only one from a node's page): once, to that host — no failover resend.
    if (target.endpoint.id !== primary.id) return attempt(target.endpoint, id, input);
    try {
      return await attempt(primary, id, input);
    } catch (err) {
      if (!isUnreachable(err) || !isResendable(id, input.body)) throw err;
      const next = await deps.manager.whenPrimaryUp(deps.resendWaitMs);
      if (next === null) throw err;
      // Once: if the new primary does not answer either, the caller hears it (and the manager already did).
      return attempt(next, id, input);
    }
  }

  return {
    get baseUrl() {
      return deps.manager.primary().apiUrl;
    },
    call,
  };
}
